import React, { useState, useEffect } from 'react';
import {
  Box, Grid, Paper, Typography, TextField, MenuItem, Button, Chip, InputAdornment, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TablePagination, CircularProgress,
} from '@mui/material';
import { Search as SearchIcon, FileDownload as DownloadIcon, Print as PrintIcon } from '@mui/icons-material';
import * as XLSX from 'xlsx';
import { accountingAPI } from '../../services/api';
import { money, label, errorText } from '../../utils/format';
import { fmtDate } from '../../utils/dates';
import { printPage, esc } from '../../utils/print';
import { MiniStat, PAYMENT_MODES } from './common';

const TYPE_STYLE = {
  FEE: { label: 'Student fee', bg: '#e0f2f1', fg: '#00695c' },
  INCOME: { label: 'Other income', bg: '#e1f5fe', fg: '#0277bd' },
  EXPENSE: { label: 'Expense', bg: '#fff3e0', fg: '#e65100' },
  SALARY: { label: 'Salary', bg: '#f3e5f5', fg: '#6a1b9a' },
};

export default function DayBookTab({ range, notify }) {
  const [type, setType] = useState('ALL');
  const [mode, setMode] = useState('ALL');
  const [q, setQ] = useState('');
  const [debounced, setDebounced] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  useEffect(() => { const t = setTimeout(() => setDebounced(q), 350); return () => clearTimeout(t); }, [q]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await accountingAPI.getLedger({ from: range.from, to: range.to, type, mode, q: debounced });
        if (!cancelled) { setData(r.data); setPage(0); }
      } catch (e) { notify(errorText(e, 'Could not load the day book'), 'error'); }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [range.from, range.to, type, mode, debounced, notify]);

  const rows = data?.rows || [];
  const shown = rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  const hasBalance = data && !data.filtered;

  const exportExcel = () => {
    const sheet = rows.map((r) => ({
      Date: r.date, Type: TYPE_STYLE[r.type]?.label || r.type, Reference: r.ref || '', 'Paid by / to': r.party || '',
      Category: label(r.category), Details: r.description || '', Mode: label(r.mode),
      'Received (₹)': Number(r.in), 'Paid (₹)': Number(r.out), ...(hasBalance ? { 'Balance (₹)': Number(r.balance) } : {}),
    }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(sheet), 'Day Book');
    XLSX.writeFile(wb, `day_book_${range.from}_to_${range.to}.xlsx`);
  };

  const print = () => {
    const body = `
      <div class="grid">
        <div class="card"><div class="v">${money(data.openingBalance)}</div><div class="l">Opening balance</div></div>
        <div class="card"><div class="v green">${money(data.totalIn)}</div><div class="l">Total received</div></div>
        <div class="card"><div class="v orange">${money(data.totalOut)}</div><div class="l">Total paid</div></div>
        ${hasBalance ? `<div class="card"><div class="v">${money(data.closingBalance)}</div><div class="l">Closing balance</div></div>` : ''}
      </div>
      <table><thead><tr><th>Date</th><th>Type</th><th>Ref.</th><th>Paid by / to</th><th>Category</th><th>Details</th><th>Mode</th><th class="r">Received</th><th class="r">Paid</th>${hasBalance ? '<th class="r">Balance</th>' : ''}</tr></thead><tbody>
      ${rows.map((r) => `<tr><td>${esc(fmtDate(r.date))}</td><td>${esc(TYPE_STYLE[r.type]?.label || r.type)}</td><td>${esc(r.ref)}</td><td>${esc(r.party)}</td><td>${esc(label(r.category))}</td><td>${esc(r.description)}</td><td>${esc(label(r.mode))}</td><td class="r">${Number(r.in) ? money(r.in) : ''}</td><td class="r">${Number(r.out) ? money(r.out) : ''}</td>${hasBalance ? `<td class="r b">${money(r.balance)}</td>` : ''}</tr>`).join('')}
      </tbody></table>`;
    printPage('Day Book', `${fmtDate(range.from)} to ${fmtDate(range.to)}`, body);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2, justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField select size="small" label="Type" value={type} onChange={(e) => setType(e.target.value)} sx={{ minWidth: 150 }}>
            <MenuItem value="ALL">All entries</MenuItem>
            {Object.entries(TYPE_STYLE).map(([k, v]) => <MenuItem key={k} value={k}>{v.label}</MenuItem>)}
          </TextField>
          <TextField select size="small" label="Payment mode" value={mode} onChange={(e) => setMode(e.target.value)} sx={{ minWidth: 150 }}>
            <MenuItem value="ALL">All modes</MenuItem>
            {PAYMENT_MODES.map((m) => <MenuItem key={m} value={m}>{label(m)}</MenuItem>)}
          </TextField>
          <TextField size="small" placeholder="Search name, receipt, category..." value={q} onChange={(e) => setQ(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} sx={{ minWidth: 260 }} />
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" size="small" startIcon={<DownloadIcon />} onClick={exportExcel} disabled={!rows.length}>Export Excel</Button>
          <Button variant="outlined" size="small" startIcon={<PrintIcon />} onClick={print} disabled={!rows.length}>Print</Button>
        </Box>
      </Box>

      {data && (
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={6} md={3}><MiniStat label="Opening balance" value={money(data.openingBalance)} /></Grid>
          <Grid item xs={6} md={3}><MiniStat label={data.filtered ? 'Received (filtered)' : 'Total received'} value={money(data.totalIn)} color="#00796b" bg="#e8f5e9" /></Grid>
          <Grid item xs={6} md={3}><MiniStat label={data.filtered ? 'Paid (filtered)' : 'Total paid'} value={money(data.totalOut)} color="#ef6c00" bg="#fff3e0" /></Grid>
          <Grid item xs={6} md={3}>
            {hasBalance ? <MiniStat label="Closing balance" value={money(data.closingBalance)} color="#0277bd" bg="#e1f5fe" />
              : <MiniStat label="Entries shown" value={data.count} color="#546e7a" bg="#eceff1" />}
          </Grid>
        </Grid>
      )}
      {data?.filtered && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          Running balance is hidden while filters are on, because it only makes sense for the complete day book.
        </Typography>
      )}

      <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
        {loading ? <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box> : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell><TableCell>Type</TableCell><TableCell>Reference</TableCell><TableCell>Paid by / to</TableCell>
                  <TableCell>Category</TableCell><TableCell>Details</TableCell><TableCell>Mode</TableCell>
                  <TableCell align="right">Received</TableCell><TableCell align="right">Paid</TableCell>
                  {hasBalance && <TableCell align="right">Balance</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {shown.map((r, i) => {
                  const st = TYPE_STYLE[r.type] || TYPE_STYLE.EXPENSE;
                  return (
                    <TableRow key={`${r.type}-${r.date}-${i}-${page}`} hover>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtDate(r.date)}</TableCell>
                      <TableCell><Chip size="small" label={st.label} sx={{ bgcolor: st.bg, color: st.fg, fontWeight: 700 }} /></TableCell>
                      <TableCell>{r.ref || '-'}</TableCell>
                      <TableCell>{r.party || '-'}</TableCell>
                      <TableCell>{label(r.category)}</TableCell>
                      <TableCell sx={{ maxWidth: 240 }}>{r.description || '-'}</TableCell>
                      <TableCell>{label(r.mode) || '-'}</TableCell>
                      <TableCell align="right" sx={{ color: '#00796b', fontWeight: 700 }}>{Number(r.in) ? money(r.in) : ''}</TableCell>
                      <TableCell align="right" sx={{ color: '#ef6c00', fontWeight: 700 }}>{Number(r.out) ? money(r.out) : ''}</TableCell>
                      {hasBalance && <TableCell align="right" sx={{ fontWeight: 700 }}>{money(r.balance)}</TableCell>}
                    </TableRow>
                  );
                })}
                {shown.length === 0 && (
                  <TableRow><TableCell colSpan={hasBalance ? 10 : 9} align="center" sx={{ py: 5, color: 'text.secondary' }}>No entries for this period and filter</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
            <TablePagination component="div" count={rows.length} page={page} onPageChange={(e, p) => setPage(p)} rowsPerPage={rowsPerPage}
              rowsPerPageOptions={[25, 50, 100]} onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }} />
          </TableContainer>
        )}
      </Paper>
    </Box>
  );
}
